import LightbulbIcon from '@mui/icons-material/Lightbulb';
import { Box } from '@ovComponents/1-primative/box/box';
import { IconButton } from '@ovComponents/2-component/iconButton/iconButton';
import { SecondaryTabs } from '@ovComponents/3-pattern/tabNav/tabNav';
import { useThemeTokens } from '@providers/themeTokenProvider';
import CommandStrip from './components/commandStrip';
import FlowBreadcrumb from './components/flowBreadcrumb';
import InsightSidebar from './components/insightSidebar';
import OperationsTab from './components/operationsTab';
import PortfoliosTab from './components/portfoliosTab';
import TodaysActivityTab from './components/todaysActivityTab';
import TodaysActivityTabV2 from './components/todaysActivityTabV2';
import TradeRequestsTab from './components/tradeRequestsTab';
import { TradingHubProvider, useTradingHub } from './tradingHubContext';
import type { TabId } from './types';
import { CORE_TABS } from './utils/constants';

export type TodaysTabVariant = 'v1' | 'v2';

const HubContent = ({
  todaysTabVariant = 'v1',
}: {
  todaysTabVariant?: TodaysTabVariant;
}) => {
  const { sys } = useThemeTokens();
  const {
    currentTab,
    setCurrentTab,
    insightSidebarOpen,
    setInsightSidebarOpen,
  } = useTradingHub();

  const isV2 = todaysTabVariant === 'v2';

  const renderTabContent = () => {
    switch (currentTab) {
      case 'today':
        return isV2 ? <TodaysActivityTabV2 /> : <TodaysActivityTab />;
      case 'portfolios':
        return <PortfoliosTab />;
      case 'operations':
        return <OperationsTab />;
      case 'tradeRequests':
        return <TradeRequestsTab />;
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        // v2 has no CommandStrip, so the tabs would otherwise float below the
        // layout breadcrumb's bottom margin. Pull the whole hub up to close
        // that gap (v1 is unaffected).
        mt: isV2 ? -2 : 0,
      }}
    >
      {/* v2 drops the CommandStrip top bar — its market-status / AUM / status
          facts are already shown by the in-tab SnapshotMetadataHeader. */}
      {!isV2 && <CommandStrip />}

      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box sx={{ flex: 1, overflow: 'auto', px: 2, pt: isV2 ? 0 : 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              // v2 drops the CommandStrip, so pull the tabs up to close the
              // gap that bar used to occupy.
              mt: isV2 ? 0 : 3,
              mb: 2,
              overflow: 'auto',
              width: '100%',
              '& > :first-child': { mt: 0, mb: 0 },
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <SecondaryTabs
                tabs={CORE_TABS.map((tab) => ({
                  label: tab.name,
                  icon: '',
                  key: tab.accessor,
                }))}
                activeTab={currentTab}
                setActiveTab={(tab) => setCurrentTab(tab as TabId)}
              />
            </Box>
            {isV2 && (
              <IconButton
                size='small'
                onClick={() => setInsightSidebarOpen(!insightSidebarOpen)}
                label='Toggle Insights'
              >
                <LightbulbIcon
                  sx={{
                    fontSize: 16,
                    color: insightSidebarOpen
                      ? sys.color.warning
                      : sys.color.onSurfaceVariant,
                  }}
                />
              </IconButton>
            )}
          </Box>

          {currentTab !== 'today' && <FlowBreadcrumb />}

          <Box mt={2} pb={3}>
            {renderTabContent()}
          </Box>
        </Box>

        {insightSidebarOpen && (
          <Box
            sx={{
              width: 360,
              borderLeft: '1px solid',
              borderColor: 'divider',
              overflow: 'auto',
              flexShrink: 0,
            }}
          >
            <InsightSidebar />
          </Box>
        )}
      </Box>
    </Box>
  );
};

const PortfolioTradingHub = ({
  todaysTabVariant = 'v1',
}: {
  todaysTabVariant?: TodaysTabVariant;
}) => {
  return (
    <TradingHubProvider>
      <HubContent todaysTabVariant={todaysTabVariant} />
    </TradingHubProvider>
  );
};

export default PortfolioTradingHub;
